import React, { useEffect, useState } from "react";
import axios from "../services/api";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { FaEdit, FaTrash, FaPlus, FaTimes } from "react-icons/fa";

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({
    id: null,
    title: "",
    department: "",
    embed_url: "",
    company_id: "",
    is_active: true,
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterCompany, setFilterCompany] = useState("");

  useEffect(() => {
    fetchReports();
    fetchCompanies();
    fetchDepartments();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/reports/");
      setReports(res.data);
    } catch {
      toast.error("Failed to fetch reports");
    }
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get("/companies/");
      setCompanies(res.data);
    } catch {
      toast.error("Failed to fetch companies");
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get("/departments/");
      setDepartments(res.data);
    } catch {
      toast.error("Failed to fetch departments");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: form.title,
        department: form.department,
        embed_url: form.embed_url,
        company_id: form.company_id,
        is_active: form.is_active,
      };

      if (editing) {
        await axios.put(`/reports/${form.id}`, payload);
        toast.success("Report updated");
      } else {
        await axios.post("/reports/", payload);
        toast.success("Report created");
      }
      resetForm();
      fetchReports();
    } catch {
      toast.error("Operation failed");
    }
    setLoading(false);
  };

  const handleEdit = (r) => {
    setForm({ ...r, id: r.id });
    setEditing(true);
  };

  const confirmDelete = (id) => {
    setDeleteTarget(id);
  };

  const performDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`/reports/${deleteTarget}`);
      toast.success("Report deleted");
      fetchReports();
    } catch {
      toast.error("Failed to delete");
    }
    setDeleteTarget(null);
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      id: null,
      title: "",
      department: "",
      embed_url: "",
      company_id: "",
      is_active: true,
    });
    setEditing(false);
  };

  const filtered = reports.filter((r) => {
    const matchesSearch = r.title.toLowerCase().includes(search.toLowerCase());
    const matchesDept = !filterDept || r.department === filterDept;
    const matchesCompany = !filterCompany || r.company_id === filterCompany;
    return matchesSearch && matchesDept && matchesCompany;
  });

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">📊 Manage Reports</h2>

      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by title..."
          className="border p-2 flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="border p-2" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.name}>{d.name}</option>
          ))}
        </select>
        <select className="border p-2" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
          <option value="">All Companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-100 p-4 rounded mb-6"
      >
        <input type="text" placeholder="Title" className="border p-2" value={form.title} required onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <select
          className="border p-2"
          required
          value={form.department}
          onChange={(e) => setForm({ ...form, department: e.target.value })}
        >
          <option value="">Select Department</option>
          {departments.map((d) => (
            <option key={d.id} value={d.name}>{d.name}</option>
          ))}
        </select>
        <input type="url" placeholder="Embed URL" className="border p-2" value={form.embed_url} required onChange={(e) => setForm({ ...form, embed_url: e.target.value })} />
        <select className="border p-2" value={form.company_id} required onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
          <option value="">Select Company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <label className="flex items-center px-2">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="mr-2" /> Active
        </label>
        <div className="flex gap-2 col-span-1 md:col-span-2">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
            disabled={loading}
          >
            <FaPlus /> {editing ? "Update" : "Create"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded flex items-center gap-2"
            >
              <FaTimes /> Cancel
            </button>
          )}
        </div>
      </form>

      {loading && (
        <div className="flex justify-center items-center my-4">
          <div className="h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {!loading && filtered.length > 0 ? (
        <div className="grid gap-4">
          {filtered.map((r) => {
            const company = companies.find((c) => c.id === r.company_id);
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white p-4 rounded shadow flex justify-between items-start"
              >
                <div>
                  <p className="font-semibold text-lg">{r.title}</p>
                  <p className="text-sm text-gray-600">{r.department}</p>
                  <p className="text-xs text-gray-500">Company: {company?.name || r.company_id}</p>
                  <a
                    href={r.embed_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 underline text-sm mt-1 inline-block"
                  >
                    View in Power BI
                  </a>
                </div>
                <div className="text-right flex flex-col gap-2">
                  <button onClick={() => handleEdit(r)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <FaEdit /> Edit
                  </button>
                  <button onClick={() => confirmDelete(r.id)} className="text-red-600 hover:text-red-800 flex items-center gap-1">
                    <FaTrash /> Delete
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        !loading && <p className="text-gray-500 italic">No reports found.</p>
      )}

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white p-6 rounded shadow-md w-96 text-center"
            >
              <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
              <p className="mb-4">Are you sure you want to delete this report?</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteTarget(null)} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
                <button onClick={performDelete} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
